import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/apiClient';
import Navbar from '../components/Navbar';
import ModulePill from '../components/ModulePill';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';

/**
 * Authenticity Roll-up (Phase 1.7i/j) — faculty-facing single pane of
 * glass showing paste-ratio, revision-richness, classification mix, and
 * top student / topic risk signals across every captured submission.
 */
const NAVY = '#1A2B4C';
const GOLD = '#FCD359';
const GOLD_DARK = '#E0B73D';

const CLASS_COLOR = {
  authentic: '#16A34A',
  watch: '#F59E0B',
  high_concern: '#DC2626',
};
const CLASS_LABEL = {
  authentic: 'Authentic',
  watch: 'Watch',
  high_concern: 'High concern',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(iso); }
};

// ─── Detail view of one submission ────────────────────────────────────────
const PostDetail = ({ replyId, onClose }) => {
  const [post, setPost] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const r = await api.get(`/api/admin/authenticity/post/${encodeURIComponent(replyId)}`);
        if (!cancel) setPost(r.data);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || 'Failed to load post.');
      }
    };
    load();
    return () => { cancel = true; };
  }, [replyId]);

  if (err) return <div className="card p-6 text-red-600" data-testid="authn-detail-error">{err}</div>;
  if (!post) return <div className="card p-6 text-gray-500" data-testid="authn-detail-loading">Loading submission…</div>;

  const tele = post.telemetry || {};
  return (
    <div data-testid="authn-detail">
      <button onClick={onClose} className="text-sm font-semibold mb-3" style={{ color: NAVY }}>
        ← Back to authenticity list
      </button>

      <div className="card p-5 mb-4">
        <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: GOLD_DARK }}>
          Submission detail
        </div>
        <div className="text-lg font-bold mb-3" style={{ color: NAVY }}>{post.thread_title}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4">
          <Stat label="Author" value={post.author_name} />
          <Stat label="Submitted" value={fmtDate(post.created_at)} />
          <Stat label="Classification"
                value={CLASS_LABEL[post.classification] || post.classification}
                colour={CLASS_COLOR[post.classification]} />
          <Stat label="Length" value={`${post.body_length} chars`} />
          <Stat label="Paste ratio" value={`${post.paste_ratio}%`}
                colour={post.paste_ratio >= 30 ? CLASS_COLOR.high_concern : NAVY} />
          <Stat label="Revision richness" value={`${post.revision_richness} / 100`} />
          <Stat label="Revision count" value={tele.revision_count ?? 0} />
          <Stat label="Focused time" value={`${Math.round((tele.focused_seconds || 0) / 60 * 10) / 10}m`} />
        </div>

        {(tele.paste_events || []).length > 0 && (
          <div className="mb-3">
            <div className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: NAVY }}>
              Paste events ({tele.paste_events.length})
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {(tele.paste_events || []).map((p, i) => (
                <div key={i} className="text-xs border rounded p-2"
                     style={{ background: 'rgba(252,211,89,0.10)', borderColor: 'var(--gr-border)' }}>
                  <div className="text-gray-500">
                    +{p.length} chars · @ {Math.round((p.at_ms || 0) / 1000)}s
                  </div>
                  {p.snippet && (
                    <div className="font-mono text-[11px] mt-1 text-gray-700 italic">
                      &ldquo;{p.snippet.slice(0, 120)}&rdquo;
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: NAVY }}>
          Submission text
        </div>
        <div className="p-3 rounded text-sm whitespace-pre-wrap leading-relaxed"
             style={{ background: 'var(--gr-bone-2)', border: '1px solid var(--gr-border)' }}>
          {post.body}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, colour }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-base font-bold mt-0.5" style={{ color: colour || NAVY }}>{value}</div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────
const AuthenticityPage = () => {
  const { replyId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState('');
  const [days, setDays] = useState(60);

  useEffect(() => {
    if (replyId) return;
    let cancel = false;
    const load = async () => {
      setLoading(true);
      try {
        const [s, p] = await Promise.all([
          api.get(`/api/admin/authenticity/stats?days=${days}`),
          api.get(`/api/admin/authenticity/posts?days=${days}&limit=150${classFilter ? `&classification=${classFilter}` : ''}`),
        ]);
        if (cancel) return;
        setStats(s.data);
        setPosts(p.data?.posts || []);
      } catch (e) {
        if (!cancel) console.error('Authenticity load failed', e);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [days, classFilter, replyId]);

  const classPie = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.classification_counts || {})
      .map(([name, value]) => ({ name: CLASS_LABEL[name] || name, raw: name, value }));
  }, [stats]);
  const pasteBars = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.paste_buckets || {}).map(([bucket, count]) => ({ bucket, count }));
  }, [stats]);
  const revBars = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.revision_buckets || {}).map(([bucket, count]) => ({ bucket, count }));
  }, [stats]);

  if (replyId) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--gr-bone-2)' }}>
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-6">
          <PostDetail replyId={replyId} onClose={() => navigate('/authenticity')} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--gr-bone-2)' }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-6" data-testid="authenticity-page">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }} data-testid="authenticity-title">
              Authenticity Roll-up
            </h1>
            <div className="text-sm text-gray-600 mt-1 max-w-2xl">
              Paste-ratio, revision-richness, and per-submission classification across every captured
              discussion reply. Signal — never verdict — to support faculty judgement.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Window</label>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                    className="input-field py-1 px-2 text-sm" data-testid="authn-window-select">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="card p-10 text-center text-gray-500" data-testid="authn-loading">Loading…</div>
        ) : !stats || stats.total_submissions === 0 ? (
          <div className="card p-10 text-center" data-testid="authn-empty">
            <div className="text-lg font-bold mb-1" style={{ color: NAVY }}>No captured submissions yet</div>
            <div className="text-sm text-gray-600 max-w-md mx-auto">
              Authenticity signals are captured automatically when students post in discussion threads
              using the telemetry-enabled editor. As soon as posts roll in, they&apos;ll appear here.
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Kpi label="Total submissions" value={stats.total_submissions} accent={NAVY} />
              <Kpi label="Authentic" value={stats.classification_counts.authentic} accent={CLASS_COLOR.authentic} />
              <Kpi label="Watch" value={stats.classification_counts.watch} accent={CLASS_COLOR.watch} />
              <Kpi label="High concern" value={stats.classification_counts.high_concern} accent={CLASS_COLOR.high_concern} />
              <Kpi label="Avg paste %" value={`${stats.avg_paste_ratio}%`} accent={GOLD_DARK} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <ChartCard title="Classification mix">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={classPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                      {classPie.map((entry) => (
                        <Cell key={entry.raw} fill={CLASS_COLOR[entry.raw] || NAVY} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Paste ratio distribution">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={pasteBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={GOLD_DARK} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Revision count distribution">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={revBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={NAVY} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {stats.daily_volume?.length > 1 && (
              <div className="mb-4">
                <ChartCard title="Daily submission volume + high-concern overlay">
                  <ResponsiveContainer width="100%" height={210}>
                    <LineChart data={stats.daily_volume}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="count" name="All" stroke={NAVY} strokeWidth={2}
                            dot={{ fill: GOLD, stroke: NAVY }} />
                      <Line type="monotone" dataKey="high_concern" name="High concern"
                            stroke={CLASS_COLOR.high_concern} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            )}

            {(stats.student_profile?.length > 0 || stats.topic_heatmap?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {stats.student_profile?.length > 0 && (
                  <div className="card p-4">
                    <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
                      Per-student profile (ranked by concern)
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                          <th className="pb-2">Student</th>
                          <th className="pb-2 text-right">Posts</th>
                          <th className="pb-2 text-right">Avg paste</th>
                          <th className="pb-2 text-right">Avg revisions</th>
                          <th className="pb-2 text-right">High concern</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.student_profile.slice(0, 12).map((s) => (
                          <tr key={s.author_email} className="border-t" style={{ borderColor: 'var(--gr-border)' }}>
                            <td className="py-2 text-xs">{s.author_name}</td>
                            <td className="py-2 text-right">{s.count}</td>
                            <td className="py-2 text-right font-bold"
                                style={{ color: s.avg_paste >= 30 ? CLASS_COLOR.high_concern : NAVY }}>
                              {s.avg_paste}%
                            </td>
                            <td className="py-2 text-right">{s.avg_revision_richness}</td>
                            <td className="py-2 text-right font-bold"
                                style={{ color: s.high_concern > 0 ? CLASS_COLOR.high_concern : NAVY }}>
                              {s.high_concern}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {stats.topic_heatmap?.length > 0 && (
                  <div className="card p-4">
                    <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
                      Topic heatmap (highest concern at top)
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                          <th className="pb-2">Module</th>
                          <th className="pb-2">Topic</th>
                          <th className="pb-2 text-right">Posts</th>
                          <th className="pb-2 text-right">Avg paste</th>
                          <th className="pb-2 text-right">Concern %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.topic_heatmap.slice(0, 12).map((t) => (
                          <tr key={t.topic_id} className="border-t" style={{ borderColor: 'var(--gr-border)' }}>
                            <td className="py-2 font-mono text-[11px]">{t.module_id || '—'}</td>
                            <td className="py-2 font-mono text-[11px]">{t.topic_id}</td>
                            <td className="py-2 text-right">{t.count}</td>
                            <td className="py-2 text-right">{t.avg_paste}%</td>
                            <td className="py-2 text-right font-bold"
                                style={{ color: t.high_concern_rate >= 30 ? CLASS_COLOR.high_concern : NAVY }}>
                              {t.high_concern_rate}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="card p-4" data-testid="authn-posts-card">
              <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
                <div className="text-sm font-bold uppercase tracking-wider" style={{ color: NAVY }}>
                  Submissions ({posts.length})
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Classification</label>
                  <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
                          className="input-field py-1 px-2 text-sm" data-testid="authn-class-filter">
                    <option value="">All</option>
                    <option value="authentic">Authentic</option>
                    <option value="watch">Watch</option>
                    <option value="high_concern">High concern</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                      <th className="pb-2">Excerpt</th>
                      <th className="pb-2">Student</th>
                      <th className="pb-2">Module</th>
                      <th className="pb-2 text-right">Paste</th>
                      <th className="pb-2 text-right">Revs</th>
                      <th className="pb-2">Class</th>
                      <th className="pb-2">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.reply_id}
                          className="border-t hover:bg-yellow-50 cursor-pointer"
                          style={{ borderColor: 'var(--gr-border)' }}
                          onClick={() => navigate(`/authenticity/${encodeURIComponent(p.reply_id)}`)}
                          data-testid={`authn-row-${p.reply_id}`}>
                        <td className="py-2 max-w-xs truncate" title={p.body_excerpt}>{p.body_excerpt || '—'}</td>
                        <td className="py-2 text-xs">{p.author_name}</td>
                        <td className="py-2 text-xs">
                          {p.module_id ? <ModulePill compact>{p.module_id}</ModulePill> : '—'}
                        </td>
                        <td className="py-2 text-right font-bold"
                            style={{ color: p.paste_ratio >= 30 ? CLASS_COLOR.high_concern : NAVY }}>
                          {p.paste_ratio}%
                        </td>
                        <td className="py-2 text-right">{p.revision_count}</td>
                        <td className="py-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded"
                                style={{
                                  background: `${CLASS_COLOR[p.classification]}1a`,
                                  color: CLASS_COLOR[p.classification],
                                }}>
                            {CLASS_LABEL[p.classification] || p.classification}
                          </span>
                        </td>
                        <td className="py-2 text-xs text-gray-500">{fmtDate(p.created_at)}</td>
                      </tr>
                    ))}
                    {posts.length === 0 && (
                      <tr><td colSpan={7} className="py-6 text-center text-gray-500">No submissions in this filter.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const Kpi = ({ label, value, accent }) => (
  <div className="card p-4">
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color: accent || NAVY }}>{value}</div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="card p-4">
    <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>{title}</div>
    {children}
  </div>
);

export default AuthenticityPage;
